import { useEffect, useRef } from "react";
import { useBlocker } from "react-router-dom";

/**
 * Guards against losing unsaved form changes.
 *
 * - Blocks in-app (React Router) navigation while `isDirty` is true, exposing a
 *   blocker the caller can use to show a confirmation modal.
 * - Warns on browser-level navigation (tab close / refresh) via `beforeunload`.
 *
 * `isDirty` is mirrored into a ref so the blocker callback always reads the
 * latest value synchronously — this avoids a stale closure causing the guard to
 * fire on a programmatic navigation that happens right after the form is saved
 * (e.g. `setSaved(true); navigate(...)` in the same React batch).
 *
 * The returned blocker has `state` ("blocked" | "unblocked" | "proceeding"),
 * `proceed()` to continue the navigation, and `reset()` to cancel it.
 */
export function useUnsavedChanges(isDirty: boolean) {
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirtyRef.current && currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (!isDirty) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Required for some browsers to show the native prompt.
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  return blocker;
}
